import { Component, createRef } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import { isMobileOnly } from 'react-device-detect';

import loadable from '../../utils/loadable';

import * as storyActions from '../../actions/stories.actions';
import * as storyFiltersActions from '../../actions/storyFilters.action';
import * as commonActions from '../../actions/common.actions';
import * as preferencesActions from '../../actions/preferencesActions';
import withComponentName from '../../decorators/withComponentName';

import {
  periodValues,
  defaultValues,
  STORIES_LIMIT,
} from '../../data/filters/storyFilters';

import StoriesSvc from '../../services/StoriesSvc';
import StorageSvc from '../../services/StorageSvc';

import StoryCard from './StoryCard';
import StoryFilters from '../StoryFilters';
import RecommendedStories from '../RecommendedStories';
import NewStoriesButton from '../Buttons/NewStoriesButton';
import Preloader from '../Preloader';
import StoryCaughtUp from '../StoryCaughtUp';
import Summarisation from '../Summarisation';
import OpenFiltersButton from '../StoryFilters/StoryFiltersButton/OpenFiltersButton';

import { compareFilters, compareUserViewFilters } from '../../helpers/helpers';
import { getBrowserScrollTop } from '../../helpers/commonHelpers';

import Styles from './styles.module.scss';
import { filterSelectedTimeFilter } from '../../helpers/timeFilterHelpers';
import { filterSelectedSentimentForStories } from '../../helpers/storyHelpers';
import { SILVER } from '../../data/permissions';
import TopicsSvc from '../../services/TopicsSvc';
import { getEvents } from '../../services/dbServices/filtersDataService';

const Masonry = loadable(() => import('react-masonry-component'));
const cx = classNames.bind(Styles);

class TrendingStories extends Component {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      scrollPosition: 0,
      initialStoriesLoaded: false,
      newStoriesAvailable: false,
      seeMoreStoriesTop: 153,
      position: 'absolute',
      pageToken: null,
      summaryRequestMethod: null,
      isSummaryHidden: false,
    };

    this.isTabActive = false;
    this.loadStoriesTimeout = null;
    this.cancelToken = axios.CancelToken;
    this.sourceCancelToken = this.cancelToken.source();
    this.isScrolling = null;
    this.newStoriesFetching = false;
    this.storyUpdateTimer = 0;
    this.storiesRef = createRef();
  }

  componentDidMount() {
    const { storyFilters } = this.props;
    const rehydrate = JSON.parse(StorageSvc.getItem('expandStories'));
    this.setState({
      ...rehydrate,
    });
    this.isTabActive = true;
    if (storyFilters.filtersInitialized) {
      this.loadStories(storyFilters);
    }
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('focus', this.onFocus, { passive: true });
    window.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentDidUpdate(prevProps) {
    const {
      storyFilters,
      scrollUpPressed,
      actions,
      promobarHeight,
      token,
    } = this.props;
    const { newStoriesAvailable } = this.state;

    if (promobarHeight !== prevProps.promobarHeight && newStoriesAvailable) {
      this.onScroll();
    }

    if (storyFilters.filtersInitialized && !compareFilters(storyFilters, prevProps.storyFilters)) {
      this.setState({
        newStoriesAvailable: false,
        pageToken: null,
      });
      this.scrollToStart(false);
      this.loadStories(storyFilters);
    }

    if (scrollUpPressed) {
      actions.toggleScrollUpState(false);
      this.scrollToStart(false);
    }

    if (!token && !compareUserViewFilters(storyFilters, prevProps.storyFilters)) {
      this.forceUpdate();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.loadStoriesTimeout);
    clearTimeout(this.storyUpdateTimer);
    this.isTabActive = false;
    if (this.sourceCancelToken) this.sourceCancelToken.cancel();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('visibilitychange', this.onVisibilityChange);
    const { actions } = this.props;
    actions.resetStoriesReduxData();
    actions.freezeStories(false);
    actions.toggleStoryFilters(false);
  }

  handleRefreshEvent() {
    const { storyFilters } = this.props;

    this.setState({
      newStoriesAvailable: false,
      pageToken: null,
    });
    this.scrollToStart(false);
    this.loadStories({
      ...storyFilters,
      isRefresh: true,
    });
  }

  onVisibilityChange() {
    if (document.hidden) {
      clearTimeout(this.loadStoriesTimeout);
      if (this.sourceCancelToken) this.sourceCancelToken.cancel();
      this.isTabActive = false;
    } else {
      if (!this.sourceCancelToken) this.sourceCancelToken = this.cancelToken.source();
      this.isTabActive = true;
      const { storyFilters } = this.props;
      this.loadStories(storyFilters);
    }
  }

  onFocus() {
    const { hasStoryError } = this.state;
    if (hasStoryError) {
      this.setState({
        pageToken: null,
        newStoriesAvailable: false,
      }, () => {
        const { storyFilters } = this.props;
        this.loadStories(storyFilters);
      });
    }
  }

  onScroll() {
    const { scrollPosition } = this.state;
    const {
      actions, freezeStories, promobarHeight, stories,
    } = this.props;
    const topOffset = 150;
    const fixed = topOffset - getBrowserScrollTop() > 15;
    this.setState({
      seeMoreStoriesTop: fixed ? 150 : (15 + promobarHeight),
      position: fixed ? 'absolute' : 'fixed',
    });

    clearTimeout(this.isScrolling);

    this.isScrolling = setTimeout(() => {
      if (Math.abs(scrollPosition - window.scrollY) > 250 && freezeStories) {
        actions.freezeStories(false);
      }

      this.setState({
        scrollPosition: window.scrollY,
      });
    }, 100);

    const docTop = this.storiesRef.current.offsetTop;
    const docHeight = this.storiesRef.current.offsetHeight;
    const winheight = window.innerHeight === undefined
      ? document.documentElement.clientHeight
      : window.innerHeight;
    const scrollpoint = window.scrollY === undefined
      ? window.document.documentElement.scrollTop
      : window.scrollY;

    const storiesTop = this.storiesRef.current.getBoundingClientRect().top;
    const isFilterFixed = (storiesTop - 40) <= 0;

    this.setState({
      isFilterFixed,
    });

    if ((scrollpoint + winheight) >= (docHeight + docTop) && stories.length > 0) {
      this.onScrollLoadData();
    }
  }

  async onScrollLoadData() {
    const { loading, storyFilters } = this.props;
    const { initialStoriesLoaded, pageToken } = this.state;
    if (this.newStoriesFetching || loading || !initialStoriesLoaded) return;

    if (pageToken !== 'EOD') {
      this.newStoriesFetching = true;
      await this.loadStories(storyFilters, true);
      this.newStoriesFetching = false;
    }
  }

  getCurrentPeriodSelection() {
    const { storyFilters } = this.props;
    return periodValues.find((item) => item.value === storyFilters.time_filter);
  }

  getParams(filters) {
    const {
      accessLevels, isAdvancedLayout, activeSentimentTab,
    } = this.props;

    const params = {
      ...filters,
    };

    // filter keys that values are empty
    Object.keys(params).forEach((key) => {
      const hasSilverAccess = accessLevels.includes(SILVER);
      const forwardKeys = [
        'order_by', 'languages', 'min_score',
        'categories', 'twitter_following_only', 'with_links',
        'asset_classes', 'sectors', 'industries', 'subindustries',
        'startup_categories', 'segments', 'events', 'translate_to',
        'premium_only', 'page_token', 'selected_time_filter',
        'blacklisted_domain_ids', 'whitelisted_domain_ids', 'domains_filtering_mode',
        ...filterSelectedTimeFilter(filters.selected_time_filter),
        ...filterSelectedSentimentForStories(filters, hasSilverAccess),
        'isRefresh',
      ];
      if (isAdvancedLayout && activeSentimentTab !== 'all') {
        params.sentiment_levels = activeSentimentTab;
      }
      if (!params[key] || forwardKeys.indexOf(key) === -1) {
        delete params[key];
      } else if (filters.all_languages) {
        delete params.languages;
      }
    });

    params.all_languages = Boolean(filters.all_languages);
    params.premium_only = filters.premium_content_only;
    params.fold_similar_stories = filters.group_similar_stories;

    return params;
  }

  async loadStories(filters, nextPage = false) {
    const {
      storyFilters,
      freezeStories,
      stories,
      actions,
    } = this.props;
    if (!storyFilters.conflictsChecked) return;
    const { isFilterFixed } = this.state;

    clearTimeout(this.loadStoriesTimeout);
    // Load stories when only when second timeout call when be the same value and not frozen
    if (
      filters.refresh_rate === storyFilters.refresh_rate
      && !freezeStories
      && (!filters.isTimeout || (filters.isTimeout && !isFilterFixed))
      && this.isTabActive
    ) {
      if (!stories?.length) {
        actions.activateTrendingPreloader();
      }

      await this.fetchStories(filters, nextPage);
    }

    if (filters.isScrollTop) {
      clearTimeout(this.storyUpdateTimer);
      this.storyUpdateTimer = 0;
      this.loadStoriesTimeout = setTimeout(this.loadStories, filters.refresh_rate,
        { ...storyFilters, isTimeout: true });
      return;
    }

    if (filters.refresh_rate !== 'never' && Number(filters.refresh_rate) && this.isTabActive) {
      if (nextPage || isFilterFixed) {
        if (!this.storyUpdateTimer) {
          this.storyUpdateTimer = setTimeout(this.checkForUpdate, filters.refresh_rate);
        }
      } else {
        clearTimeout(this.storyUpdateTimer);
        this.storyUpdateTimer = 0;
        this.loadStoriesTimeout = setTimeout(this.loadStories, filters.refresh_rate,
          { ...storyFilters, isTimeout: true });
      }
    }
  }

  async checkForUpdate() {
    const { storyFilters, stories, earliestSearchDate } = this.props;

    const params = this.getParams(storyFilters);
    const { stories: newStories, cancelled } = await StoriesSvc
      .getStories(
        params,
        earliestSearchDate,
        {
          skipSmallList: true,
          ...(this.sourceCancelToken ? { token: this.sourceCancelToken.token } : null),
        },
      );

    if (cancelled) return;

    const nonDuplicateStoriesIds = new Set([
      ...stories.slice(0, STORIES_LIMIT).map(({ id }) => id),
      ...newStories.map(({ id }) => id),
    ]);

    if (nonDuplicateStoriesIds.size > STORIES_LIMIT) {
      this.setState({
        newStoriesAvailable: true,
      });
    } else {
      const { isFilterFixed } = this.state;
      if (isFilterFixed) {
        this.storyUpdateTimer = setTimeout(this.checkForUpdate, storyFilters.refresh_rate);
      } else {
        clearTimeout(this.storyUpdateTimer);
        this.storyUpdateTimer = 0;
        this.loadStoriesTimeout = setTimeout(this.loadStories, storyFilters.refresh_rate,
          { ...storyFilters, isTimeout: true });
      }
    }
  }

  async getSelectedClasses(isEventId) {
    const selectedClasses = JSON.parse(StorageSvc.getItem('asset_classesFilter'))?.selectedItems;
    let allAssetClasses;
    await TopicsSvc.getAssetsClasses().then(({ data }) => {
      allAssetClasses = data.asset_classes;
    });

    if (isEventId) {
      const event = allAssetClasses.filter((item) => item.name === 'events');
      return event[0].id;
    }
    allAssetClasses = allAssetClasses.filter((item) => item.name !== 'events');
    if (selectedClasses?.length
      && selectedClasses?.length !== allAssetClasses.length) {
      return selectedClasses.map((item) => item.id);
    }
  }

  async getSelectedEvents() {
    const eventClassId = await this.getSelectedClasses(true);

    const selectedEvents = JSON.parse(StorageSvc.getItem('eventsFilter'))?.selectedItems;
    let allEventsFilter;
    if (eventClassId) {
      await getEvents(1, eventClassId, 200)
        .then(({ data }) => {
          allEventsFilter = data.assets;
        });
    }

    if (selectedEvents?.length && selectedEvents?.length !== allEventsFilter.length) {
      return selectedEvents.map((item) => item.id);
    }
  }

  async fetchStories(filters, nextPage = false) {
    const {
      stories: currentStories, actions, earliestSearchDate, isTrendingPage,
    } = this.props;
    const {
      initialStoriesLoaded, pageToken,
    } = this.state;

    let classIds;
    let eventIds;

    if (currentStories?.length) actions.fetchTrendingStoriesBegin();

    const params = {
      ...this.getParams(filters),
      ...(nextPage && {
        page_token: pageToken,
      }),
    };

    if (isTrendingPage) {
      classIds = await this.getSelectedClasses(false);
      eventIds = await this.getSelectedEvents();
      params.asset_classes = classIds?.toString();
      params.events = eventIds?.toString();
    }

    if (
      filters.empty_selected_trending_filters || !filters.categories
      || !filters.languages
    ) {
      // Show empty stories block if there isn't any
      // trending filter or source filter or language selected
      actions.fetchTrendingStoriesSuccess([]);
      const newState = {
        pageToken: 'EOD',
      };
      if (!initialStoriesLoaded) {
        newState.initialStoriesLoaded = true;
      }
      this.setState(newState);
    } else {
      try {
        if (!nextPage) {
          clearTimeout(this.loadStoriesTimeout);
          clearTimeout(this.storyUpdateTimer);

          this.setState({
            summaryRequestMethod: (limit = 2, options = {}) => (
              StoriesSvc
                .getStories({
                  ...params,
                  only_summaries: true,
                  number_of_summaries: limit,
                  ...options,
                }, earliestSearchDate)
            ),
          });
        }

        if (this.sourceCancelToken) this.sourceCancelToken.cancel('');

        this.cancelToken = axios.CancelToken;
        this.sourceCancelToken = this.cancelToken.source();

        const {
          stories,
          nextPageToken,
          cancelled,
          error = false,
        } = await StoriesSvc.getStories(
          params,
          earliestSearchDate,
          {
            cancelToken: this.sourceCancelToken.token,
            callCurationActions: true,
            skipSmallList: true,
          },
        );

        this.setState({
          hasStoryError: error && !cancelled,
        });

        if (cancelled) return;
        // reset access token value
        this.sourceCancelToken = null;

        actions.fetchTrendingStoriesSuccess(
          nextPage ? [
            ...currentStories,
            ...stories,
          ] : (
            stories
          ),
        );
        this.setState({
          pageToken: nextPageToken,
          isSummaryHidden: !stories.length,
        });
        if (!initialStoriesLoaded) this.setState({ initialStoriesLoaded: true });
        this.checkIfNoStoriesWithCurrentFilter(stories.length);
      } catch (e) {
        console.log(e.message);
      }
    }
  }

  checkIfNoStoriesWithCurrentFilter(storiesLength) {
    const { token, storyFilters } = this.props;

    if (
      (
        (!storyFilters.time_filter && token)
      )
      && storiesLength === 0
    ) {
      // return source cancel token because we removed it
      this.sourceCancelToken = this.cancelToken.source();

      const currentSelection = this.getCurrentPeriodSelection();

      let currentSelectionIndex = (
        periodValues
          .map(({ value }) => value)
          .indexOf(currentSelection.value)
      );
      const nextSelectionIndex = (
        currentSelectionIndex <= periodValues.length - 1 ? (
          (currentSelectionIndex += 1, currentSelectionIndex)
        ) : (
          periodValues.length - 1
        )
      );
      defaultValues.periodFilterValue = periodValues[nextSelectionIndex];
    }
  }

  scrollToStart(fetchNewStories = true) {
    const { storyFilters } = this.props;

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    this.setState({
      newStoriesAvailable: false,
    });
    if (fetchNewStories) {
      this.loadStories({ ...storyFilters, isScrollTop: true });
    }
  }

  render() {
    const {
      initialStoriesLoaded,
      newStoriesAvailable,
      seeMoreStoriesTop,
      position,
      pageToken,
      summaryRequestMethod,
      isSummaryHidden,
    } = this.state;
    const {
      stories,
      actions,
      loading,
      activatePreloader,
      piwikEnabled,
      user,
      storyFilters,
      onSimplifiedViewTabsClick,
      isAdvancedLayout,
      width,
    } = this.props;

    const isCompactView = isMobileOnly ? (
      storyFilters.user_newsfeed_compactview
    ) : (
      storyFilters.user_newsfeed_compactview_web
    );
    const userNewsfeedView = isMobileOnly ? (
      storyFilters.user_newsfeed_view
    ) : (
      storyFilters.user_newsfeed_view_web
    );
    const isTilesView = userNewsfeedView === 'tiles' && !isCompactView;
    const storiesClass = cx({
      TendingStories: true,
      tiles: isTilesView,
    });

    const headCardsClass = cx({
      'TendingStories__head--cards': true,
      'stories-loading': loading && !this.newStoriesFetching,
      tiles: isTilesView,
    });

    const allowMasonry = isTilesView && width > 767;
    const StoriesWrapper = allowMasonry ? Masonry : 'div';
    const storiesWrapperProps = {
      className: cx({
        tiles_wrapper: isTilesView,
        without_tiles: !isTilesView,
        without_compact: !isCompactView,
      }),
      ...(allowMasonry && {
        disableImagesLoaded: true,
        options: {
          gutter: 10,
          percentPosition: true,
          resize: false,
          isResizeBound: false,
          transitionDuration: 0,
        },
        ref: (c) => { this.masonry = this.masonry || c.masonry; },
      }),
    };

    return (
      <div className={storiesClass} ref={this.storiesRef}>
        <NewStoriesButton
          className={cx('new-stories-btn', { visible: newStoriesAvailable })}
          onClick={this.scrollToStart}
          style={{
            position,
            top: `${seeMoreStoriesTop}px`,
          }}
        />
        <div className={Styles.TendingStories__head}>
          <OpenFiltersButton
            refreshEvent={this.handleRefreshEvent}
            filterBtnText={width < 1025 ? 'Filters' : 'Feed Filters'}
            feedFilters
            onSimplifiedViewTabsClick={onSimplifiedViewTabsClick}
            isSimpleLayout={isAdvancedLayout}
            classname="feedFilter"
          />
        </div>
        <div>
          <StoryFilters
            onRef={(ref) => { this.storyFilters = ref; }}
            user={user}
            onSimplifiedViewTabsClick={onSimplifiedViewTabsClick}
            isSimpleLayout={isAdvancedLayout}
            piwikEnabled={piwikEnabled}
          />
        </div>
        <div className={Styles['stories-upgrade-suggestion']} />
        <Summarisation
          storiesEndpoint={summaryRequestMethod}
          isTilesView={isTilesView}
          isHidden={isSummaryHidden}
        />
        <div className={headCardsClass}>
          {!initialStoriesLoaded || activatePreloader ? (
            <Preloader
              loading
              static
              centered
            />
          ) : (
            <div className={cx('stories', { tiles: isTilesView })}>
              <StoriesWrapper {...storiesWrapperProps}>
                {stories.map((story, index) => (
                  <StoryCard
                    key={story.id}
                    user={user}
                    type={story.type}
                    story={story}
                    onHide={actions.hideStory}
                    onFreezeStories={actions.freezeStories}
                    onLikeChanged={actions.updateStoriesInfo}
                    onBookmarkClick={actions.updateStoriesBookmark}
                    piwikEnabled={piwikEnabled}
                    loading={loading}
                    masonryObj={this.masonry}
                    index={index}
                    trendingPageStories
                    hasBigScreenDesign
                  />
                ))}
              </StoriesWrapper>
              {loading && this.newStoriesFetching && (
                <Preloader
                  loading
                  static
                  centered
                />
              )}
              {stories.length < 20 && pageToken === 'EOD' && initialStoriesLoaded && !activatePreloader && (
                <RecommendedStories
                  storiesLength={stories.length}
                  loadingStories={loading}
                  trendingPage
                  isAdvancedView={isAdvancedLayout}
                  oldStories={stories}
                  trendingPageStories
                />
              )}
              {
                stories.length >= 30 && pageToken === 'EOD' && (
                  <StoryCaughtUp hideButton />
                )
              }
            </div>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps({
  stories,
  storyFilters,
  common,
  watchlistReducer,
  promobar,
  subscriptions,
}) {
  return {
    loading: stories.loading,
    stories: StoriesSvc.filterStories(stories.cards, storyFilters),
    freezeStories: stories.freeze_stories,
    activatePreloader: stories.activatePreloader,
    storyFilters,
    scrollUpPressed: common.scrollUpPressed,
    width: common.width,
    promobarHeight: promobar.height,
    token: watchlistReducer.userToken,
    earliestSearchDate: subscriptions.permissions.earliest_search_date,
    accessLevels: subscriptions.permissions.access_levels,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      ...storyActions,
      ...storyFiltersActions,
      ...commonActions,
      ...preferencesActions,
    }, dispatch),
  };
}

export default withComponentName(connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true },
)(TrendingStories));
